import FilterValues from './filtervalues.js';
import ColorSelectorFilter from "./colorselectorfilter.js";
import SelectedFilters from "./selectedfilters.js";
import PriceRangeFilter from "./pricerangefilter.js";
import SubSearchFilter from "./subsearchfilter.js";
import ProductQuantityFilter from "./productquantityfilter.js";
import UpdateFilterUrl from "./updatefilterurl.js";
import FilterUIHelpers from "./filteruihelpers.js";
import DataService from "./dataservice.js";
import { prodListValues } from './shared.js';

const overflow = 10;
const menuContainer = document.getElementById('menuContainer');
const filterMenuMobile = document.getElementById('filterMenuMobile');
const hiddenTags = document.getElementById('hiddenTags');
const mobileFilterDropDown = document.getElementById('filterDropDown');
const productContainer = document.getElementById('productContainer');

let openMenuOption;

class FilterMenu {
    /**
     * Gets the menu container element.
     * @returns {HTMLElement} menuContainer
     */
    get getMenuContainer() {
        return menuContainer;
    };

    /**
     * Gets the currently open menu option element.
     * @returns {HTMLElement} openMenuOption
     */
    get openMenuOption() {
        return openMenuOption;
    };

    /**
     * Marks the given element as selected and updates the filter values.
     * @param {HTMLElement} element 
     * @param {boolean} logFilter - Pass in false if the filter does not need to be logged
     * @returns {void}
     */
    markSelected(element, logFilter) {
        if (FilterValues.isColorId(element.id)) {
            ColorSelectorFilter.update(element);
        } else {
            if (typeof (logFilter) === 'undefined') logFilter = true;
            element.checked = true;
            FilterValues.addFilter(element.id, logFilter);
            SelectedFilters.addToSelectedFilter(element);
        }
    };

    /**
     * Marks the given element as unselected and updates the filter values.
     * @param {HTMLElement} element 
     * @returns {void}
     */
    markUnSelected(element) {
        element.checked = false;
        FilterValues.removeFilter(element.id);
        SelectedFilters.removeFromSelectedFilter(element);
    };

    /**
     * Sets the filter order by updating the sort dropdown and filter values.
     * @param {string} id 
     * @param {boolean} logFilter 
     * @returns {void}
     */
    setFilterOrder(id, logFilter) {
        if (typeof (logFilter) === 'undefined') logFilter = true;
        const element = document.getElementById('sortDropDown');
        element.value = id;
        FilterValues.addFilter(id, logFilter);
    };

    /**
     * Sets the page size by updating the items per page dropdown and filter values.
     * @param {string} id 
     * @returns {void}
     */
    setPageSize(id) {
        document.getElementById('itemsPerPageDropDown').value = id;
        FilterValues.addPageSize(id);
    };

    /**
     * Marks a group of filters as unselected.
     * @param {Array<string>} list 
     * @returns {void}
     */
    markGroupUnSelectedFilter(list) {
        let element;
        for (let i = 0; i < list.length; i++) {
            element = document.getElementById(list[i]);
            element.checked = false;
        }
    };

    /**
     * Unselects all filters and updates the URL if needed.
     * @param {boolean} updateUrl 
     * @returns {void}
     */
    unSelectAllFilters(updateUrl) {
        const colorFilterValues = FilterValues.getColorFilter();
        let colorValuesWithElementID = [];
        for (let i = 0; i < colorFilterValues.length; i++) {
            colorValuesWithElementID.push(FilterValues.getColorElementId(colorFilterValues[i]));
        }

        const ratingFilterValues = FilterValues.getRatingFilter();
        let ratingValuesWithElementID = [];
        for (let i = 0; i < ratingFilterValues.length; i++) {
            ratingValuesWithElementID.push(FilterValues.getRatingElementId(ratingFilterValues[i]));
        }

        const productTimeFilterValues = FilterValues.getProdTimeFilter();
        let productTimeValuesWithElementID = [];
        for (let i = 0; i < productTimeFilterValues.length; i++) {
            productTimeValuesWithElementID.push(FilterValues.getProductionTimeElementId(productTimeFilterValues[i]));
        }

        const imprintLocationCountFilterValues = FilterValues.getImprintLocationCountFilter();
        let imprintLocationCountValuesWithElementID = [];
        for (let i = 0; i < imprintLocationCountFilterValues.length; i++) {
            imprintLocationCountValuesWithElementID.push(FilterValues.getImprintLocationCountElementId(imprintLocationCountFilterValues[i]));
        }

        const imprintColorCountFilterValues = FilterValues.getImprintColorCountFilter();
        let imprintColorCountValuesWithElementID = [];
        for (let i = 0; i < imprintColorCountFilterValues.length; i++) {
            imprintColorCountValuesWithElementID.push(FilterValues.getImprintColorCountElementId(imprintColorCountFilterValues[i]));
        }

        const tagFilterValues = FilterValues.getTagFilter();
        let tagValuesWithElementID = [];
        for (let i = 0; i < tagFilterValues.length; i++) {
            tagValuesWithElementID.push(FilterValues.getTagElementId(tagFilterValues[i]));
        }

        this.markGroupUnSelectedFilter(ratingValuesWithElementID);
        this.markGroupUnSelectedFilter(productTimeValuesWithElementID);
        this.markGroupUnSelectedFilter(imprintLocationCountValuesWithElementID);
        this.markGroupUnSelectedFilter(imprintColorCountValuesWithElementID);
        this.markGroupUnSelectedFilter(tagValuesWithElementID);
        ColorSelectorFilter.clear(colorValuesWithElementID);
        if (document.getElementById('PriceRange') !== null) {
            PriceRangeFilter.clear();
        }
        SubSearchFilter.clear();

        ProductQuantityFilter.clear();
        FilterValues.clearFilters();
        SelectedFilters.removeAllFromSelectedFilter();

        if (updateUrl) {
            UpdateFilterUrl.update(FilterValues, prodListValues, false);
        }
    };

    /**
     * Selects the main menu and updates its state.
     * @param {HTMLElement} menu 
     * @returns {void}
     */
    selectMainMenu(menu) {
        menu.classList.add('active');
        menu.setAttribute('aria-expanded', 'true');

        let element = document.getElementById(menu.getAttribute('aria-controls'));

        // if paging exists on the page, we need to move the menu up by the height of the paging so that it appears right under the trigger button
        //only on mobile and tablet size
        if(window.innerWidth < 800) {
            const topPaging = document.querySelector('.pagingWrapper');
            const hasTopPaging = topPaging.childElementCount > 0;
            if(hasTopPaging) element.parentNode.style.top = '-' + topPaging.offsetHeight + 'px';
        }

        element.classList.remove('hide');

        let listItems = element.querySelectorAll('li:not(.hide)');

        if (listItems.length) {
            setTimeout(function () {
                listItems[0].children[0].focus({ preventScroll: true });
            }, 100);
            listItems[0].children[0].addEventListener('keydown', this.shiftTabKeyPress);
            listItems[listItems.length - 1].children[0].addEventListener('keydown', this.tabKeyPress);
        }
    };

    /**
     * Unselects the main menu and updates its state.
     * @param {HTMLElement} menu 
     * @returns {void}
     */
    unSelectMainMenu(menu) {
        menu.classList.remove('active');
        menu.setAttribute('aria-expanded', 'false');

        let element = document.getElementById(menu.getAttribute('aria-controls'));

        let listItems = element.querySelectorAll('li:not(.hide)');

        if (listItems.length) {
            listItems[0].children[0].removeEventListener('keydown', this.shiftTabKeyPress);
            listItems[listItems.length - 1].children[0].removeEventListener('keydown', this.tabKeyPress);
        }

        element.classList.add('hide');

        menuContainer.querySelectorAll('.filterMenu').forEach(element => {
            this.hideFilterOption(element);
        });
    };

    /**
     * Hides the given filter option element based on its overflow state.
     * @param {HTMLElement} element 
     * @returns {void}
     */
    hideFilterOption(element) {
        if (element.classList.contains('overflow')) {
            element.classList.add('hideOnSmallAndMedium'); //todo remove
            element.classList.add('show-md');
        } else {
            element.classList.add('showOnLarge'); //todo remove
            element.classList.add('show-md');
        }
    };

    /**
     * Selects the filter menu and updates its state.
     * @param {HTMLElement} menu 
     * @returns {void}
     */
    selectFilterMenu(menu) {
        if (menu) {
            menu.classList.remove('showOnLarge'); //todo remove
            menu.classList.remove('show-md');
            menu.classList.remove('hideOnSmallAndMedium'); //todo remove
            menu.classList.remove('hide');

            const listItems = menu.querySelectorAll('li:not(.hide)');

            if (listItems.length) {
                listItems[0].children[0].focus();
                listItems[0].children[0].addEventListener('keydown', this.shiftTabKeyPress);
                listItems[listItems.length - 1].children[0].addEventListener('keydown', this.tabKeyPress);
            } else {
                openMenuOption = menu;
                menu.querySelector('.filterMenuSelect').disabled = false;
                menu.querySelector('.filterMenuSelect').focus();
                menu.querySelector('.filterMenuSelect').addEventListener('keydown', this.shiftTabKeyPress);

                menu.querySelectorAll('.lastObject').forEach(element => {
                    element.addEventListener('keydown', this.tabKeyPress);
                });
            }
        }
    };

    /**
     * Unselects the filter menu and updates its state.
     * @param {HTMLElement} element 
     * @returns {void}
     */
    unSelectFilterMenu(element) {
        if (!element?.parentElement?.parentElement) return;
        const listItems = element.parentElement.parentElement.querySelectorAll('li:not(.hide)');

        if (listItems.length) {
            listItems[0].children[0].removeEventListener('keydown', this.shiftTabKeyPress);
            listItems[listItems.length - 1].children[0].removeEventListener('keydown', this.tabKeyPress);
        }

        let parent = element.parentElement.parentElement;

        if (parent.classList.contains('filterMenu')) {
            this.hideFilterOption(parent);
            parent.querySelector('.filterMenuSelect').removeEventListener('keydown', this.shiftTabKeyPress);
            parent.querySelector('.filterMenuSelect').disabled = true;

            parent.querySelectorAll('.lastObject').forEach(element => {
                element.removeEventListener('keydown', this.tabKeyPress);
            });
        } else {
            parent.classList.add('hide');
        }
    };

    /**
     * Marks the selected filters based on the currentIds and newIds.
     * @param {Array<string>} currentIds 
     * @param {Array<string>} newIds 
     * @param {Function} getId 
     * @returns {void}
     */
    markSelectedReload(currentIds, newIds, getId) {
        let filters = '';

        for (let i = 0; i < newIds.length; i++) {
            if (currentIds.indexOf(newIds[i]) === -1) {
                filters += '#' + getId(newIds[i]) + ',';
            }
        }

        if (filters.length) {
            let list = document.querySelectorAll(filters.substr(0, filters.length - 1));

            for (let j = 0; j < list.length; j++) {
                this.markSelected(list[j], false);
            }
        }
    };

    /**
     * Marks the unselected filters based on the currentIds and newIds.
     * @param {Array<string>} currentIds 
     * @param {Array<string>} newIds 
     * @param {Function} getId 
     * @param {number} endIndex 
     * @returns {void}
     */
    markUnSelectedReload(currentIds, newIds, getId, endIndex) {
        let filters = '';

        for (let i = 0; i < endIndex; i++) {
            if (newIds.indexOf(currentIds[i]) === -1) {
                filters += '#' + getId(currentIds[i]) + ',';
            }
        }

        if (filters.length) {
            let list = document.querySelectorAll(filters.substr(0, filters.length - 1));

            for (let j = 0; j < list.length; j++) {
                this.markUnSelected(list[j]);
            }
        }
    };

    /**
     * Updates the menu selections based on the provided options.
     * @param {Object} options 
     * @returns {void}
     */
    updateMenuSelections(options) {
        const mainMenu = filterMenuMobile.querySelectorAll('a:not(.moreLink):not(.noHide)');
        let mainMenuCnt = 0;

        this.addTagFilters();

        for (let i = 0; i < mainMenu.length; i++) {
            if (this.updateMenuOptions(mainMenu[i].getAttribute('aria-controls'), options)) {
                mainMenu[i].parentElement.classList.add('hide');
                mainMenu[i].parentElement.classList.remove('overflow');
            } else {
                if ((++mainMenuCnt) <= overflow) {
                    mainMenu[i].parentElement.classList.remove('hide');
                    mainMenu[i].parentElement.classList.remove('overflow');
                } else {
                    mainMenu[i].parentElement.classList.add('hide');
                    mainMenu[i].parentElement.classList.add('overflow');
                }
            }
        }

        if (mainMenuCnt > overflow) {
            this.resetMoreLink(filterMenuMobile.querySelectorAll('.moreLink'));
        } else {
            filterMenuMobile.querySelectorAll('.moreLink').forEach(element => {
                element.parentElement.classList.add('hide');
            });
        }
    };

    /**
     * Updates the menu options based on the provided options.
     * @param {string} menuId 
     * @param {Object} options 
     * @returns {boolean}
     */
    updateMenuOptions(menuId, options) {
        // Check if options is defined and has the menuId property
        // console.log(options.hasOwnProperty(menuId));
        if (!options || !options.hasOwnProperty(menuId)) {
            return false; // Return false if options is undefined or menuId is not a property of options
        }

        const filterOptions = options[menuId];

        if (menuId === 'colorFilter') {
            ColorSelectorFilter.updateCounts(filterOptions);
            return false;
        }

        if (document.getElementById(menuId)) {
            const filters = [].slice.call(document.getElementById(menuId).querySelectorAll('input[type=checkbox], input[type=radio]'));
            if (filters.length) {
                let optionCnt;
                let filterId = '';
                for (let i = 0; i < filters.length; i++) {
                    optionCnt = 0;
                    filterId = FilterValues.getFilterId(filters[i].id);
                    if (filterId && filterOptions) {
                        for (let j = 0; j < filterOptions.length; j++) {
                            if (filterOptions[j].Id === filterId) {
                                optionCnt = filterOptions[j].Count;
                                break;
                            }
                        }

                    }
                    FilterUIHelpers.getUpdatedFilterCount(document.getElementById(filters[i].id + '_label'), optionCnt, filters[i].getAttribute('data-filter'));
                }
            }
            return false;
        }
        return false;
    };

    /**
     * Resets the "More" link state.
     * @param {HTMLCollection} moreLink 
     * @returns {void}
     */
    resetMoreLink(moreLink) {
        if (moreLink.length) {
            let element = moreLink[0];
            element.parentElement.classList.remove('hide');

            if (element.getAttribute('aria-expanded') === 'true') {
                let newLabel = element.innerHTML.replace("Less", "More");
                element.innerHTML = newLabel;
                element.querySelector("svg").setAttribute("class", "rotate180");
                element.setAttribute('aria-expanded', 'false');
            }
        }
    };

    /**
     * Handles the Tab key press event.
     * @param {KeyboardEvent} e 
     * @returns {void}
     */
    tabKeyPress(e) {
        if (e.which === 9 && !e.shiftKey) {
            const element = this.parentElement?.parentElement;
            if (!element) return;
            const listItems = element.querySelectorAll('li:not(.hide)');

            if (listItems.length) {
                e.preventDefault();
                listItems[0].children[0].focus();
            } else if (mobileFilterDropDown.classList.contains('active') && getComputedStyle(element.querySelector('.filterMenuSelect'), null).display !== 'none') {
                e.preventDefault();
                element.querySelector('.filterMenuSelect').focus();
            }
        }
    };

    /**
     * Handles the Shift+Tab key press event.
     * @param {KeyboardEvent} e 
     * @returns {void}
     */
    shiftTabKeyPress(e) {
        if (e.which === 9 && e.shiftKey) {
            e.preventDefault();

            mobileFilterDropDown.focus();
        }
    };

    /**
     * Toggles the filter options menu.
     * @param {Event} e 
     * @returns {void}
     */
    toggleFilterOptions(e) {
        e.preventDefault();
        const btn = e.currentTarget;
        menuContainer.removeAttribute("style");
        if (menuContainer.classList.contains('hide')) {
            // if paging exists on the page, we need to move the menu up by the height of the paging so that it appears right under the trigger button
            //only on mobile and tablet size
            if(window.innerWidth < 800) {
                const topPaging = document.querySelector('.pagingWrapper');
                const hasTopPaging = topPaging.childElementCount > 0;
                if(hasTopPaging) menuContainer.parentNode.style.top = '-' + topPaging.offsetHeight + 'px';
            }

            menuContainer.classList.remove('hide');
            productContainer.classList.remove('c-md-13');
            productContainer.classList.add('c-md-10');
            btn.setAttribute('aria-expanded', true);

            const accordionHeader = menuContainer.querySelector('.filterMenu button.accordionHeader');
            if (accordionHeader) {
                accordionHeader.focus();
                accordionHeader.removeEventListener("click", this.toggleFilterOptions);
            }
        } else {
            menuContainer.classList.add('hide');
            productContainer.classList.add('c-md-13');
            productContainer.classList.remove('c-md-10');
            btn.setAttribute('aria-expanded', false);
        }

        FilterUIHelpers.toggleButtonLabel(btn);
    };

    /**
     * Toggles the tag filters.
     * @param {Event} e 
     * @returns {void}
     */
    toggleTagFilters(e) {
        e.preventDefault();
        const btn = e.currentTarget;

        let filterId = "MoreFiltersButton", filtervalue = "More";

        const expanded = (btn.getAttribute('aria-expanded') === 'true');

        if (mobileFilterDropDown.classList.contains('active')) {
            mobileFilterDropDown.click();
        }

        btn.setAttribute('aria-expanded', !expanded);
        let newLabel = btn.innerHTML;

        if (expanded) {
            filtervalue = "Less";
            btn.innerHTML = newLabel.replace("Less", "More");
            btn.querySelector("svg").setAttribute("class", "rotate180");
            DataService.filterLog(filterId, filtervalue);
        } else {
            btn.innerHTML = newLabel.replace("More", "Less");
            btn.querySelector("svg").removeAttribute("class");
            DataService.filterLog(filterId, filtervalue);
            this.addTagFilters();
            setTimeout(function () {
                document.getElementById("tagFiltersDiv").querySelector("button").focus();
            }, 100);
        }

        menuContainer.querySelectorAll('.filterMenu.overflow').forEach(element => {
            if (expanded) {
                element.classList.add('hide');
                element.classList.remove('hideOnSmallAndMedium'); //todo remove
                element.classList.remove('show-md');
            } else {
                element.classList.remove('hide');
                element.classList.add('hideOnSmallAndMedium'); //todo remove
                element.classList.add('show-md');
            }
        });
    };

    /**
     * Handles the click event on the "More" link.
     * @param {Event} event 
     * @param {HTMLElement} element 
     * @returns {void}
     */
    moreLinkClickEvent(event, element) {
        event.preventDefault();

        let parent = element.parentElement.parentElement;
        let newLabel;

        if (element.getAttribute('aria-expanded') === 'true') {
            parent.querySelectorAll('.overflow').forEach(element => {
                element.classList.add('hide');
            });
            newLabel = element.innerHTML.replace("Less", "More");
            element.setAttribute('aria-expanded', 'false');
            element.innerHTML = newLabel;
            element.querySelector("svg").setAttribute("class", "rotate180");
        } else {
            let overflow = parent.querySelectorAll('.overflow');

            if (overflow.length) {
                overflow.forEach(el => {
                    el.classList.remove('hide');
                });

                overflow[0].children[0].focus();
            }
            newLabel = element.innerHTML.replace("More", "Less");
            element.setAttribute('aria-expanded', 'true');
            element.innerHTML = newLabel;
            element.querySelector("svg").removeAttribute("class");
        }
    };

    /**
     * Handles the click event on a checkbox.
     * @param {HTMLInputElement} element 
     * @returns {void}
     */
    checkboxClickEvent(element) {
        if (element.checked) {
            this.markSelected(element);
        } else {
            this.markUnSelected(element);
        }

        if (mobileFilterDropDown.classList.contains('active')) {
            mobileFilterDropDown.click();
        }

        UpdateFilterUrl.update(FilterValues, prodListValues, false);
    };

    /**
     * Handles the click event on a mobile menu select.
     * @param {Event} event 
     * @param {HTMLElement} element 
     * @returns {void}
     */
    mobileMenuSelectEvent(event, element) {
        event.preventDefault();

        if (document.querySelector(".openedFilter")) {
            document.querySelector(".openedFilter").setAttribute("aria-expanded", "false");
            document.querySelector(".openedFilter").classList.remove("openedFilter");
        }
        this.unSelectFilterMenu(element);
        this.selectFilterMenu(document.getElementById(element.getAttribute('aria-controls')));
    };

    /**
     * Initializes event listeners for the filter menu.
     * @returns {void}
     */
    events() {
        menuContainer.querySelectorAll('.filterMenuSelect').forEach(element => {
            element.addEventListener('click', (e) => {
                this.mobileMenuSelectEvent(e, element);
            });
        });

        menuContainer.querySelectorAll('.filterMainMenuSelect').forEach(element => {
            element.addEventListener('click', (e) => {
                e.preventDefault();
                element.setAttribute("aria-expanded", "true");
                element.classList.add("openedFilter");
                this.unSelectFilterMenu(element.parentElement);
                this.selectFilterMenu(document.getElementById(element.getAttribute('aria-controls')));
                if (element != document.getElementById("a_subSearchFilter")) {
                    document.getElementById('pnlSubSearch').scrollIntoView({ behavior: 'smooth' });
                } else {
                    document.getElementById("subSearchContent").classList.add("open");
                    document.getElementById("txtInnerSearch").focus();
                }
            })
        });

        menuContainer.querySelectorAll('.moreLink').forEach(element => {
            element.addEventListener('click', (e) => {
                this.moreLinkClickEvent(e, element);
            });
        });

        menuContainer.querySelectorAll('input[type=checkbox]').forEach(element => {
            element.addEventListener('change', () => {
                this.checkboxClickEvent(element);
            });
        });
    };

    /**
     * Adds tag filters to the filter menu.
     * @returns {void}
     */
    addTagFilters() {
        if (hiddenTags && hiddenTags.textContent.length) {
            const tagFilters = document.getElementById('tagFiltersDiv');
            tagFilters.innerHTML = hiddenTags.textContent;
            hiddenTags.textContent = '';

            tagFilters.querySelectorAll('.moreLink').forEach(element => {
                element.addEventListener('click', (e) => {
                    this.moreLinkClickEvent(e, element);
                });
            });

            tagFilters.querySelectorAll('input[type=checkbox]').forEach(element => {
                element.addEventListener('change', () => {
                    this.checkboxClickEvent(element);
                });
            });

            tagFilters.querySelectorAll('.filterMenuSelect').forEach(element => {
                element.addEventListener('click', (e) => {
                    this.mobileMenuSelectEvent(e, element);
                });
            });
        }
    };
}

export default new FilterMenu();